// Settings → Storage — user-visible controls for the offline map cache.
// Shows: tile-cache size (Cache API), pin-cache count/size (IndexedDB),
// last-cache update, and a "Clear cached maps" button.
//
// Design rules (per user):
// • Only clears map/tile cache + offline-pin cache. Never touches user
//   listings, accounts, or other application data.
// • Handles browsers where caches / IndexedDB / storage.estimate are
//   unavailable — degrades gracefully with a clear message.

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Database, HardDrive, MapPin, RefreshCw, Trash2, ShieldCheck,
  Loader2, WifiOff, Bell, BellOff, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pinCacheStats, clearPinCache } from "@/lib/offlinePins";
import { useSeo } from "@/hooks/useSeo";

// Prefix that identifies the map-tile cache in the CacheStorage API. Kept in
// sync with `TILE_CACHE` in /public/sw.js. If sw.js bumps its version prefix
// we still catch it via the trailing "-tiles" suffix match.
const TILE_CACHE_SUFFIX  = "-tiles";
const API_CACHE_SUFFIX   = "-api";     // keep separate — we do NOT clear API cache

// Cache-cap options exposed to the user. `0` = unlimited (falls back to the
// SW's entry-count LRU only). Default is 25 MB — enough for ~5 neighbourhoods.
const CAP_OPTIONS = [
  { mb: 10, label: "10 MB",     sub: "1-2 neighbourhoods" },
  { mb: 25, label: "25 MB",     sub: "5 neighbourhoods · recommended" },
  { mb: 50, label: "50 MB",     sub: "1 small city" },
  { mb: 0,  label: "Unlimited", sub: "keep as many as the browser allows" },
];
const CAP_KEY  = "fn_tile_cap_mb";
const DEFAULT_CAP = 25;

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0)              return "—";
  if (bytes < 1024)                        return `${bytes} B`;
  if (bytes < 1024 * 1024)                 return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)          return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function fmtAge(ts) {
  if (!ts) return "never";
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60)   return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function StorageSettings() {
  useSeo({ title: "Offline Storage · find2near", description: "Manage cached map tiles and pins for offline use." });
  const [loading,     setLoading]     = useState(true);
  const [clearing,    setClearing]    = useState(false);
  const [supported,   setSupported]   = useState({ caches: true, idb: true, estimate: true });
  const [tileBytes,   setTileBytes]   = useState(null);   // bytes across all tile caches
  const [tileCount,   setTileCount]   = useState(0);
  const [pinStats,    setPinStats]    = useState({ count: 0, totalPins: 0, newestTs: null, oldestTs: null });
  const [storageEst,  setStorageEst]  = useState({ usage: null, quota: null });
  const [isOnline,    setIsOnline]    = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true);
  // Cap slider — persisted to localStorage, pushed to the service worker.
  const [capMb,       setCapMb]       = useState(() => {
    if (typeof localStorage === "undefined") return DEFAULT_CAP;
    const v = parseInt(localStorage.getItem(CAP_KEY) || "", 10);
    return Number.isFinite(v) && v >= 0 ? v : DEFAULT_CAP;
  });
  // Push notification permission state — driven by the user's dedicated
  // "Enable alerts" button (option Z: no auto-prompt at share creation).
  const [notifPerm,   setNotifPerm]   = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [notifBusy,   setNotifBusy]   = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    // Feature detection — fail soft everywhere.
    const hasCaches = typeof caches !== "undefined";
    const hasIdb = typeof indexedDB !== "undefined";
    const hasEstimate = !!(navigator.storage && navigator.storage.estimate);
    setSupported({ caches: hasCaches, idb: hasIdb, estimate: hasEstimate });

    // 1) Tile-cache size — sum content-length across every tile-cache bucket.
    let bytes = 0, count = 0;
    if (hasCaches) {
      try {
        const names = await caches.keys();
        const tileNames = names.filter(n => n.endsWith(TILE_CACHE_SUFFIX));
        for (const name of tileNames) {
          const cache = await caches.open(name);
          const reqs = await cache.keys();
          count += reqs.length;
          for (const req of reqs) {
            const resp = await cache.match(req);
            if (!resp) continue;
            const len = parseInt(resp.headers.get("content-length") || "0", 10);
            if (len) { bytes += len; continue; }
            // Opaque tile response — content-length is often missing. Estimate
            // ~15 KB per tile which matches real MapLibre vector tiles.
            bytes += 15 * 1024;
          }
        }
      } catch { /* ignore — leave 0 */ }
    }
    setTileBytes(bytes); setTileCount(count);

    // 2) Pin cache stats
    if (hasIdb) {
      try { setPinStats(await pinCacheStats()); } catch { /* ignore */ }
    }

    // 3) Overall storage estimate (browser-wide, includes localStorage, etc.)
    if (hasEstimate) {
      try {
        const est = await navigator.storage.estimate();
        setStorageEst({ usage: est.usage || 0, quota: est.quota || 0 });
      } catch { /* ignore */ }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online",  on);
    window.addEventListener("offline", off);
    // React Router doesn't auto-scroll to `#push` — pop it into view on mount
    // so the "Manage alerts →" deep-link from the Inbox lands on the toggle.
    if (typeof window !== "undefined" && window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
    return () => {
      window.removeEventListener("online",  on);
      window.removeEventListener("offline", off);
    };
  }, [refresh]);

  // Push the current cap to the active service worker whenever it changes,
  // and on first mount so a fresh worker starts with the user's saved value.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    localStorage.setItem(CAP_KEY, String(capMb));
    navigator.serviceWorker.ready
      .then((reg) => {
        const target = reg.active || navigator.serviceWorker.controller;
        if (target) target.postMessage({ type: "F2N_TILE_CAP_MB", mb: capMb });
      })
      .catch(() => { /* silent — SW not registered on this browser */ });
  }, [capMb]);

  // Ask for browser Notification permission. Option Z: user-initiated only.
  const requestNotif = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    if (Notification.permission === "denied") {
      toast.error("Notifications are blocked. Enable them from your browser's site settings.");
      return;
    }
    setNotifBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
      if (perm === "granted") {
        toast.success("Alerts enabled — you'll get a native ping when a nominee opens your share.");
        // Fire a test notification so the user sees exactly what they'll get.
        try {
          new Notification("find2near alerts on", {
            body: "You'll be pinged here when someone opens a document you shared.",
            icon: "/logo192.png", tag: "f2n-alerts-on",
          });
        } catch { /* silent */ }
      } else if (perm === "denied") {
        toast.warning("Alerts stayed off. You can enable them later from the browser's site settings.");
      }
    } catch { toast.error("Couldn't request permission."); }
    finally { setNotifBusy(false); }
  };

  const doClear = async () => {
    if (!window.confirm(
      "Clear ALL cached map tiles and offline pins?\n\n" +
      "Your account, listings, and other app data are NOT affected."
    )) return;
    setClearing(true);
    let tileCleared = 0, tileFail = false, pinCleared = false;
    // Only remove caches ending with `-tiles`. Never touch `-api` (we would
    // lose cached NL search / discover responses).
    if (supported.caches) {
      try {
        const names = await caches.keys();
        for (const name of names) {
          if (name.endsWith(TILE_CACHE_SUFFIX)) {
            const ok = await caches.delete(name);
            if (ok) tileCleared += 1; else tileFail = true;
          }
        }
      } catch { tileFail = true; }
    }
    if (supported.idb) {
      try { pinCleared = await clearPinCache(); } catch { pinCleared = false; }
    }
    setClearing(false);
    await refresh();
    if (tileFail && !pinCleared) {
      toast.error("Couldn't clear cache — try again from a browser tab.");
    } else {
      toast.success(
        `Cleared ${tileCleared} tile cache${tileCleared === 1 ? "" : "s"}` +
        (pinCleared ? " and offline pins" : "") + "."
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white pb-16">
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <div className="flex items-center gap-2 mb-4">
          <Link to="/nearby" className="p-2 rounded-full bg-white/10 hover:bg-white/20"
                data-testid="storage-back">
            <ArrowLeft size={16}/>
          </Link>
          <h1 className="text-lg font-black tracking-tight" data-testid="storage-title">
            Offline Storage
          </h1>
          <Link to="/settings/offline"
                className="ml-auto rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 font-black text-[10px] px-3 py-1.5"
                data-testid="storage-open-offline-maps">
            Offline Maps →
          </Link>
          <button onClick={refresh} disabled={loading}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40"
                  data-testid="storage-refresh"
                  title="Refresh">
            {loading ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
          </button>
        </div>

        {!isOnline && (
          <div className="mb-3 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-200 text-xs font-black px-3 py-2 inline-flex items-center gap-2"
               data-testid="storage-offline-note">
            <WifiOff size={12}/> You're offline — measurements still work
          </div>
        )}

        {/* Info card */}
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-[12px] text-emerald-100 mb-4 flex gap-2 items-start"
             data-testid="storage-info">
          <ShieldCheck size={14} className="mt-0.5 shrink-0"/>
          <div>
            <div className="font-black mb-0.5">Only the map cache is cleared</div>
            Your account, listings, saved places, chats, and locker documents are stored
            separately and are <span className="underline">never</span> touched by this page.
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <StatCard icon={HardDrive} label="Map tiles"
                    testid="storage-tile-size"
                    value={fmtBytes(tileBytes)}
                    sub={supported.caches ? `${tileCount} tiles cached` : "Cache API unavailable"}/>
          <StatCard icon={MapPin} label="Cached pins"
                    testid="storage-pin-count"
                    value={pinStats.totalPins.toLocaleString()}
                    sub={supported.idb
                      ? `${pinStats.count} area${pinStats.count === 1 ? "" : "s"} saved`
                      : "IndexedDB unavailable"}/>
          <StatCard icon={Database} label="App storage used"
                    testid="storage-quota"
                    value={fmtBytes(storageEst.usage)}
                    sub={supported.estimate
                      ? `of ${fmtBytes(storageEst.quota)} available`
                      : "Estimate not supported"}/>
          <StatCard icon={RefreshCw} label="Last update"
                    testid="storage-last-update"
                    value={pinStats.newestTs ? fmtAge(pinStats.newestTs) : "never"}
                    sub={pinStats.oldestTs ? `oldest: ${fmtAge(pinStats.oldestTs)}` : "no data yet"}/>
        </div>

        {/* Clear action */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/20 grid place-items-center shrink-0">
              <Trash2 size={16} className="text-red-300"/>
            </div>
            <div className="flex-1">
              <div className="font-black text-sm mb-1">Clear cached maps</div>
              <p className="text-[11px] text-white/60 leading-snug">
                Removes every cached map tile and offline pin from this browser. Handy if
                you're low on device storage or want to force a fresh download the next
                time you open the Personal Map.
              </p>
              <Button onClick={doClear} disabled={clearing || loading}
                      className="mt-3 rounded-full bg-red-500 hover:bg-red-600 text-white text-[11px] font-black h-8"
                      data-testid="storage-clear-btn">
                {clearing ? <><Loader2 size={11} className="animate-spin mr-1"/>Clearing…</> : "Clear cached maps"}
              </Button>
            </div>
          </div>
        </div>

        {/* Cache Auto-Prune (cap) — user picks how much offline storage to allow. */}
        <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="storage-cap-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 grid place-items-center shrink-0">
              <Zap size={16} className="text-emerald-300"/>
            </div>
            <div className="flex-1">
              <div className="font-black text-sm mb-1">Auto-prune tile cache</div>
              <p className="text-[11px] text-white/60 leading-snug">
                We'll automatically drop the oldest tiles once your cache passes this size — so
                you never need to remember to clear it manually.
              </p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CAP_OPTIONS.map(opt => (
                  <button key={opt.mb}
                          onClick={() => setCapMb(opt.mb)}
                          className={`rounded-2xl border p-2 text-left transition ${
                            capMb === opt.mb
                              ? "bg-emerald-500 border-emerald-500 text-slate-900"
                              : "bg-white/5 border-white/10 text-white hover:bg-white/10"
                          }`}
                          data-testid={`storage-cap-${opt.mb}`}>
                    <div className="text-[13px] font-black">{opt.label}</div>
                    <div className={`text-[10px] leading-tight ${capMb === opt.mb ? "text-slate-800/80" : "text-white/50"}`}>
                      {opt.sub}
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-white/40 mt-2" data-testid="storage-cap-current">
                Current: <span className="font-black text-white/70">
                  {capMb === 0 ? "Unlimited" : `${capMb} MB`}
                </span> · applied immediately
              </div>
            </div>
          </div>
        </div>

        {/* Browser push notifications — permission opt-in (option Z). */}
        <div id="push" className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-4 scroll-mt-16" data-testid="storage-push-card">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
              notifPerm === "granted" ? "bg-emerald-500/20" : "bg-amber-500/20"
            }`}>
              {notifPerm === "granted"
                ? <Bell size={16} className="text-emerald-300"/>
                : <BellOff size={16} className="text-amber-300"/>}
            </div>
            <div className="flex-1">
              <div className="font-black text-sm mb-1">
                Emergency handoff alerts
                <span className={`ml-2 rounded-full text-[9px] font-black uppercase px-2 py-0.5 ${
                  notifPerm === "granted" ? "bg-emerald-500/20 text-emerald-300"
                    : notifPerm === "denied" ? "bg-rose-500/20 text-rose-300"
                    : "bg-white/10 text-white/60"
                }`} data-testid="storage-push-status">{notifPerm}</span>
              </div>
              <p className="text-[11px] text-white/60 leading-snug">
                Get a native browser notification the moment a nominee opens your shared documents —
                even when this tab is closed. We only ping you when the tab isn't visible, so you
                won't get double-notified when you're already here.
              </p>
              {notifPerm !== "granted" && (
                <Button onClick={requestNotif} disabled={notifBusy || notifPerm === "denied"}
                        className="mt-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-[11px] font-black h-8"
                        data-testid="storage-push-enable-btn">
                  {notifBusy
                    ? <><Loader2 size={11} className="animate-spin mr-1"/>Requesting…</>
                    : notifPerm === "denied"
                      ? "Blocked · change in browser settings"
                      : <><Bell size={11} className="mr-1"/> Enable alerts</>}
                </Button>
              )}
              {notifPerm === "granted" && (
                <div className="mt-2 text-[10px] text-emerald-300 font-black" data-testid="storage-push-on">
                  ✓ You'll be pinged natively when a nominee opens your share.
                </div>
              )}
            </div>
          </div>
        </div>

        {(!supported.caches || !supported.idb) && (
          <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-3 text-[11px] text-white/70"
               data-testid="storage-unsupported">
            {!supported.caches && <div>• Cache Storage API isn't available in this browser — map tiles can't be cached.</div>}
            {!supported.idb    && <div>• IndexedDB isn't available — offline pins can't be saved.</div>}
            <div className="mt-1">Try a modern browser (Chrome, Safari, Firefox) for full offline support.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-black text-white/50">
        <Icon size={11}/> {label}
      </div>
      <div className="text-xl font-black mt-1 tracking-tight">{value}</div>
      <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>
    </div>
  );
}
